/**
 * PATH: packages/kanban-host/src/allocation/useKanbanBoardLanes.ts
 * FILE_ID: AIHUB-KANBAN-ALLOC-LANES-001
 * PURPOSE: Lazy-load the lanes for a SINGLE board so the right-click
 *          submenu can drill from "Board → Lane" without paying the
 *          full-list cost up-front.
 * WHY: Module B of EMAIL_KANBAN_ALLOCATION_PLAN.md. The board list
 *      response already includes a `lanes[]` array, but it can be
 *      stale (operator just renamed/added a lane in another tab) and
 *      it omits the `card_count` rollup the menu wants for the
 *      "(N cards)" hint. Re-fetching via `listLanes(boardId, {
 *      withCounts: true })` on hover keeps the menu fresh AND warms
 *      the per-board cache used by `<KanbanBoardView>` if the user
 *      navigates there next.
 *
 * FLOW:
 *   open submenu  →  trigger query  →  render lane list w/ swatch + count
 *                                       └─ on click → allocateEmailToLane
 *
 * DEPENDENCIES:
 *   - @quotech/ui/kanban : Lane shape + KanbanApi
 *   - @tanstack/react-query : per-board cache key `['kanban-lanes', id]`
 *
 * Default-lane resolution: see `pickDefaultLaneId` below — the
 * left-most non-archived lane wins (Triage on INBOX boards, "Backlog"
 * elsewhere). This mirrors the BE behaviour, which resolves the same
 * way via `board.lanes.filter(archived=False).order_by("position")
 * .first()` (e.g. `email_kanban/services/auto_classify/destinations.py`).
 * The two MUST stay in lock-step so a click-on-board and a
 * click-on-leftmost-lane are observably identical; `pickDefaultLaneId`
 * is unit-pinned in `useKanbanBoardLanes.test.ts`. (The old
 * `allocation_audit.py::pick_default_lane_id` helper was inlined on the
 * BE — kept this pointer current so the next agent doesn't chase a
 * function that no longer exists.)
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { KanbanApi, Lane } from '@quotech/ui/kanban'

export interface BoardLanesResult {
  lanes: Lane[]
  /** Convenience id for the "click the board to allocate" shortcut. */
  defaultLaneId: string | null
  isLoading: boolean
  isError: boolean
}

/**
 * Pure helper — pick the canonical "default lane" for a board.
 *
 * Rules (consumer-first):
 *   1. Skip archived lanes (the operator removed them).
 *   2. For personal INBOX boards (`boardKind === 'INBOX'`):
 *        primary inbox → first TRIAGE → leftmost by position.
 *   3. For every other board: left-most non-archived by `position`
 *      (JOB "To Do" / Backlog etc. stay position-driven).
 *
 * Returns null when the board has zero non-archived lanes; the caller
 * MUST disable the "click-the-board" affordance in that case so we
 * don't silently 400.
 */
export function pickDefaultLaneId(
  lanes: Lane[] | undefined,
  opts?: { boardKind?: string | null },
): string | null {
  if (!lanes || lanes.length === 0) return null
  const active = lanes
    .filter((l) => !l.archived)
    .slice()
    .sort((a, b) => a.position - b.position)
  if (active.length === 0) return null

  if (opts?.boardKind === 'INBOX') {
    const primary = active.find((lane) => lane.is_primary_inbox)
    if (primary) return primary.id
    const triage = active.find((lane) => lane.kind === 'TRIAGE')
    if (triage) return triage.id
  }

  return active[0]?.id ?? null
}

/**
 * React hook. `enabled` is the on-hover gate from the menu — we don't
 * want a hundred parallel lane-fetches firing as the operator skims
 * the long-tail "All boards" list.
 */
export function useKanbanBoardLanes(args: {
  api: KanbanApi
  boardId: string | null
  /** When `'INBOX'`, capture prefers primary inbox then TRIAGE. */
  boardKind?: string | null
  /** Defaults to `Boolean(boardId)`. */
  enabled?: boolean
}): BoardLanesResult {
  const { api, boardId, boardKind } = args
  const enabled = args.enabled ?? Boolean(boardId)

  const query = useQuery({
    queryKey: ['kanban-lanes', boardId],
    queryFn: () => {
      if (!boardId) return Promise.resolve<Lane[]>([])
      return api.listLanes(boardId, { withCounts: true })
    },
    enabled: enabled && Boolean(boardId),
    staleTime: 15_000,
  })

  const defaultLaneId = useMemo(
    () => pickDefaultLaneId(query.data, { boardKind }),
    [query.data, boardKind],
  )

  return {
    lanes: query.data ?? [],
    defaultLaneId,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
